import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import {
  baseTimePointOptions,
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";
import PostureTrendChart from "./PostureTrendChart";
import SecurityScoreChart from "./SecurityScoreChart";

const SecurityScoreWidget = forwardRef(({ mainTimePoint }, ref) => {
  const widgetContentRef = useRef(null);
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.THIRTY_DAYS);
  const { trackEvent, getPostureCoverage, getPostureHeatmapCoverageTrend } =
    useAPI();
  const { heatmapDisplayMode } = useContext(CustomerFeatureFlagsContext);

  const {
    data: heatmapCoverageByTimepoints,
    isLoading: isLoadingHeatmapCoverageByTimepoints,
  } = useQuery({
    queryKey: dashboardKeys.widgetType("securityScore"),
    queryFn: getPostureCoverage,
    select: (data) => data.data,
  });

  const {
    data: postureHeatmapCoverageTrendData,
    isLoading: isLoadingPostureHeatmapCoverageTrend,
  } = useQuery({
    queryKey: dashboardKeys.widgetType("securityScoreHeatmapCoverageTrend"),
    queryFn: () => getPostureHeatmapCoverageTrend(),
    select: (data) => data.data,
  });

  useEffect(() => {
    const hasValidCoverageData =
      heatmapCoverageByTimepoints?.[heatmapDisplayMode]?.[mainTimePoint];
    const hasValidTrendData =
      postureHeatmapCoverageTrendData?.[heatmapDisplayMode]?.[mainTimePoint];

    if (hasValidCoverageData && hasValidTrendData) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [
    mainTimePoint,
    heatmapDisplayMode,
    heatmapCoverageByTimepoints,
    postureHeatmapCoverageTrendData,
  ]);

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || isLoadingHeatmapCoverageByTimepoints)
        return null;

      if (!isCombinedExport) {
        trackEvent("User clicked on Export to PDF button in Security Score");
      }

      try {
        if (isCombinedExport) {
          const widgetContainer = document.createElement("div");

          const widgetHeader = createPdfHeader("Security Score Report");
          widgetContainer.appendChild(widgetHeader);

          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

          const exportButton = widgetClone.querySelector(
            '[data-export-button="true"]'
          );
          if (exportButton instanceof HTMLElement) {
            exportButton.style.display = "none";
          }

          widgetContainer.appendChild(widgetClone);

          return widgetContainer;
        } else {
          const entirePageElement = await createPdfContainer();

          const header = createPdfHeader("Security Score Report");
          entirePageElement.appendChild(header);

          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

          const exportButton = widgetClone.querySelector(
            '[data-export-button="true"]'
          );
          if (exportButton instanceof HTMLElement) {
            exportButton.style.display = "none";
          }

          entirePageElement.appendChild(widgetClone);

          const { contentWidth, contentHeight } = await measureContentForPdf(
            entirePageElement
          );

          const pdfConfig = createPdfConfig(
            generateFileName(
              "Breeze_Security_Score",
              null,
              FILE_EXTENSIONS.PDF
            ),
            contentWidth,
            contentHeight
          );

          await html2pdf().set(pdfConfig).from(entirePageElement).save();
        }
      } catch (error) {
        Toast.error("Failed to export Security Score, Please try again later");
        return null;
      }
    },
    [isLoadingHeatmapCoverageByTimepoints, trackEvent]
  );

  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "securityScore",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );
  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    heatmapCoverageByTimepoints,
    icons,
    trackEvent,
    "security-score-widget",
    null,
    [TimePoint.LATEST]
  );

  const CalculationInfo = () => {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          How is the score calculated?
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Score is derived from the heatmap calculation and represents the
          average of the combined Security Domains and Infrastructure Pillars
          scores
        </Typography>
      </Box>
    );
  };

  return (
    <WidgetWrapper
      title="Security Score"
      exportToPdf={true}
      handleExportToPdf={() => handleExportToPdf(false)}
      calculationInfo={{
        label: "How is the score calculated?",
        content: <CalculationInfo />,
      }}
      sortMenuItems={{
        data: [...sortMenuItems],
        tooltip: "View changes since selected time",
      }}
    >
      {isLoadingPostureHeatmapCoverageTrend ||
      isLoadingHeatmapCoverageByTimepoints ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <LoadingIcon size={24} />
        </Box>
      ) : (
        <Box
          ref={widgetContentRef}
          sx={{ height: "100%", width: "100%", padding: "0 24px 16px 16px" }}
        >
          <Typography variant="caption" marginLeft={1}>
            Current posture{" "}
            {baseTimePoint !== TimePoint.LATEST &&
              `vs. ${baseTimePointOptions[baseTimePoint]}`}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: "16px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "35%" }}>
              {!heatmapCoverageByTimepoints ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption">No data available</Typography>
                </Box>
              ) : (
                <SecurityScoreChart
                  mainTimePoint={mainTimePoint}
                  baseTimePoint={baseTimePoint}
                  heatmapCoverageByTimepoints={heatmapCoverageByTimepoints}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", width: "65%" }}>
              {!postureHeatmapCoverageTrendData ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption">No data available</Typography>
                </Box>
              ) : (
                <PostureTrendChart
                  height={270}
                  timeRange={baseTimePoint}
                  postureHeatmapCoverageTrendData={
                    postureHeatmapCoverageTrendData
                  }
                  isLoadingPostureHeatmapCoverageTrend={
                    isLoadingPostureHeatmapCoverageTrend
                  }
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

export default SecurityScoreWidget;
